import { useEffect, useRef, useState, useCallback } from 'react';

interface UseNavbarOverflowResult<T> {
  containerRef: React.RefObject<HTMLDivElement>;
  measurementRef: React.RefObject<HTMLDivElement>;
  rightGroupRef: React.RefObject<HTMLDivElement>;
  visibleItems: T[];
  overflowItems: T[];
}

/**
 * Custom hook to detect overflow in horizontal navigation using a hidden measurement container.
 * This approach always has all items rendered in a hidden div for accurate measurements.
 * Dynamically accounts for right group (AI Assistant, notifications, etc.) width.
 *
 * @param items - Array of navigation items
 * @param moreButtonWidth - Width of the "More" button (default: 100px)
 * @returns Container refs, visible items, and overflow items
 */
export function useNavbarOverflow<T>(
  items: T[],
  moreButtonWidth: number = 100,
  reservedSpace: number = 0,
  threshold: number = 20
): UseNavbarOverflowResult<T> {
  const containerRef = useRef<HTMLDivElement>( null );
  const measurementRef = useRef<HTMLDivElement>( null );
  const rightGroupRef = useRef<HTMLDivElement>( null );
  const [ visibleItems, setVisibleItems ] = useState<T[]>( items );
  const [ overflowItems, setOverflowItems ] = useState<T[]>([]);

  const calculateOverflow = useCallback(() => {
    if ( !containerRef.current || !measurementRef.current ) return;

    const container = containerRef.current;
    const measurementContainer = measurementRef.current;

    // Calculate actual available space by measuring the header and right group
    const header = container.closest( 'header' );
    if ( !header ) return;

    const headerWidth = header.clientWidth;

    // Measure right group width if it exists
    let rightGroupWidth = 0;
    if ( rightGroupRef.current ) {
      const styles = window.getComputedStyle( rightGroupRef.current );
      const marginLeft = parseFloat( styles.marginLeft ) || 0;
      const marginRight = parseFloat( styles.marginRight ) || 0;
      rightGroupWidth = rightGroupRef.current.offsetWidth + marginLeft + marginRight;
    }

    // Calculate actual available width for navigation
    // We need to account for: logo, app name, padding, and right group
    // Get the container's offset from left
    const containerRect = container.getBoundingClientRect();
    const headerRect = header.getBoundingClientRect();
    const leftOffset = containerRect.left - headerRect.left;

    // Available space = header width - left offset - right group width - padding buffer
    const paddingBuffer = 90; // Extra safety margin (gives breathing room before overflow)
    const availableWidth = headerWidth - leftOffset - rightGroupWidth - paddingBuffer;

    // If available width is too small, skip calculation
    if ( availableWidth <= 0 ) return;

    // Measure all items from the hidden measurement container
    const measurementChildren = Array.from( measurementContainer.children ) as HTMLElement[];

    // If we have no items to measure, show everything
    if ( measurementChildren.length === 0 ) {
      setVisibleItems( items );
      setOverflowItems([]);
      return;
    }

    // Measure each item's width (including margins)
    const itemWidths = measurementChildren.map( item => {
      const styles = window.getComputedStyle( item );
      const marginLeft = parseFloat( styles.marginLeft ) || 0;
      const marginRight = parseFloat( styles.marginRight ) || 0;
      return item.offsetWidth + marginLeft + marginRight;
    });

    // Calculate how many items fit in the available space
    let accumulatedWidth = 0;
    let overflowIndex = items.length; // Default: all items fit

    for ( let i = 0; i < itemWidths.length; i++ ) {
      const itemWidth = itemWidths[i];
      const nextAccumulatedWidth = accumulatedWidth + itemWidth;

      // Determine if we need space for the More button
      // We need it if NOT all items will be visible
      const willHaveMoreButton = ( i + 1 ) < items.length;
      const reservedForMoreButton = willHaveMoreButton ? moreButtonWidth : 0;
      const actualAvailableSpace = availableWidth - reservedForMoreButton;

      // Check if this item would overflow
      if ( nextAccumulatedWidth > actualAvailableSpace ) {
        overflowIndex = i;
        break;
      }

      accumulatedWidth = nextAccumulatedWidth;
    }

    // Update state with new visible/overflow split
    const newVisibleItems = items.slice( 0, overflowIndex );
    const newOverflowItems = items.slice( overflowIndex );

    setVisibleItems( newVisibleItems );
    setOverflowItems( newOverflowItems );
  }, [ items, moreButtonWidth ]);

  useEffect(() => {
    // Initial calculation with slight delay to ensure DOM is ready
    const initialTimeout = setTimeout( calculateOverflow, 100 );

    // Recalculate on window resize with debouncing
    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout( resizeTimeout );
      resizeTimeout = setTimeout( calculateOverflow, 100 );
    };

    // Window resize listener
    window.addEventListener( 'resize', handleResize );

    // ResizeObserver for container and right group size changes
    let containerObserver: ResizeObserver | null = null;
    let rightGroupObserver: ResizeObserver | null = null;

    if ( containerRef.current ) {
      containerObserver = new ResizeObserver(() => {
        handleResize();
      });
      containerObserver.observe( containerRef.current );
    }

    // Observe right group to detect AI Assistant button appearing/disappearing
    if ( rightGroupRef.current ) {
      rightGroupObserver = new ResizeObserver(() => {
        handleResize();
      });
      rightGroupObserver.observe( rightGroupRef.current );
    }

    return () => {
      window.removeEventListener( 'resize', handleResize );
      if ( containerObserver ) {
        containerObserver.disconnect();
      }
      if ( rightGroupObserver ) {
        rightGroupObserver.disconnect();
      }
      clearTimeout( resizeTimeout );
      clearTimeout( initialTimeout );
    };
  }, [calculateOverflow]);

  return {
    containerRef,
    measurementRef,
    rightGroupRef,
    visibleItems,
    overflowItems
  };
}
